/**
 * Phone number rules for the login screens.
 *
 * `national` mirrors the national-number patterns in
 * apps/backend/src/modules/auth/sms.service.ts (SUPPORTED_COUNTRIES). Keep the
 * two in step: a number accepted here must be accepted there, and the reverse.
 * A small table is used instead of libphonenumber-js because that library's
 * metadata is stricter than the backend for some ranges (UK mobile prefixes,
 * NANP area codes), which would reject numbers the backend accepts.
 */

export type CountryCode = "91" | "1" | "44" | "61";

export type PhoneError = "empty" | "wrongStart" | "tooLong" | "tooShort";

interface CountryRule {
	code: CountryCode;
	/** Key under auth.phoneErrors.wrongStart in the locale files. */
	id: "in" | "us" | "uk" | "au";
	/** Digits in the national number: no country code, no trunk 0. */
	length: number;
	/** The backend's national-number pattern. */
	national: RegExp;
	/** True while the digits typed so far can still become a valid number. */
	leadingOk: (digits: string) => boolean;
	placeholder: string;
}

export const COUNTRY_CODES: readonly CountryCode[] = ["91", "1", "44", "61"];

export const DEFAULT_COUNTRY_CODE: CountryCode = "91";

export const COUNTRY_RULES: Record<CountryCode, CountryRule> = {
	"91": {
		code: "91",
		id: "in",
		length: 10,
		national: /^[6-9]\d{9}$/,
		leadingOk: (digits) => /^[6-9]/.test(digits),
		placeholder: "9876543210",
	},
	"1": {
		code: "1",
		id: "us",
		length: 10,
		national: /^[2-9]\d{2}[2-9]\d{6}$/,
		// Area code starts 2-9, and so does the exchange (the fourth digit).
		leadingOk: (digits) =>
			/^[2-9]/.test(digits) && (digits.length < 4 || /[2-9]/.test(digits[3])),
		placeholder: "4155550123",
	},
	"44": {
		code: "44",
		id: "uk",
		length: 10,
		national: /^7\d{9}$/,
		leadingOk: (digits) => digits.startsWith("7"),
		placeholder: "7123456789",
	},
	"61": {
		code: "61",
		id: "au",
		length: 9,
		national: /^4\d{8}$/,
		leadingOk: (digits) => digits.startsWith("4"),
		placeholder: "412345678",
	},
};

/**
 * Validate the digits typed after the country code. A wrong leading digit is
 * reported before length because adding digits cannot fix it.
 */
export function validateNationalNumber(
	code: CountryCode,
	digits: string,
): PhoneError | null {
	const rule = COUNTRY_RULES[code];
	if (digits === "") return "empty";
	if (rule.national.test(digits)) return null;
	if (!rule.leadingOk(digits)) return "wrongStart";
	return digits.length > rule.length ? "tooLong" : "tooShort";
}

/** Build the E.164 string sent to the backend, e.g. "+14155550123". */
export function buildE164(code: CountryCode, digits: string): string {
	return `+${code}${digits}`;
}

/**
 * Normalise a phone number to E.164 for the API. A number that already has a
 * plus keeps its country code. Callers that still pass the India national
 * number (10 digits) or 91 plus that number get "+91" added; anything else is
 * passed through as digits for the backend to reject with its own message.
 */
export function toE164(phone: string): string {
	const digits = phone.replace(/\D/g, "");
	if (phone.trim().startsWith("+")) return `+${digits}`;
	if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
	if (digits.length === 10) return `+91${digits}`;
	return digits;
}
